import type {
  CreateInventoryMeterReadingGroupDto,
  SerializedInventoryMeterReadingGroup,
  UpdateInventoryMeterReadingGroupDto,
} from '@enmon/commons/lib/tools/';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { BASE_REST_API_URL } from 'common/constants';
import { BaseApiClient } from './baseApiClient';
import { parseSort } from '../utils/parseSort';

export interface ReadingGroupsFilter {
  inventoryMeterId?: number;
  location?: number;
  id?: string;
  name?: string;
  sort?: string;
  current?: number;
  pageSize?: number;
}

export interface UpdateInventoryMeterReadingGroupArg {
  id: SerializedInventoryMeterReadingGroup['id'];
  data: UpdateInventoryMeterReadingGroupDto;
}

export interface CreateInventoryMeterReadingGroupArg {
  data: CreateInventoryMeterReadingGroupDto;
}

export class InventoryMeterReadingGroupsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('inventory-meter-reading-groups', BASE_REST_API_URL, jwt);
  }

  public async findOne(
    id: SerializedInventoryMeterReadingGroup['id'],
    signal?: AbortSignal,
  ): Promise<StrapiApiResponse<SerializedInventoryMeterReadingGroup>> {
    const response = await this.axios.get<StrapiApiResponse<SerializedInventoryMeterReadingGroup>>(`${id}`, {
      params: buildQuery<SerializedInventoryMeterReadingGroup>({
        filters: {},
        populate: {
          inventory_location: {
            fields: ['id', 'name'],
          },
          inventory_meters: {
            fields: ['id'],
          },
        },
      }),
      signal,
    });

    return response.data;
  }

  public async find(
    filter: ReadingGroupsFilter,
    signal?: AbortSignal,
  ): Promise<StrapiApiResponse<SerializedInventoryMeterReadingGroup>> {
    const { inventoryMeterId, location, id, name, sort, current, pageSize } = filter;

    const parsedSort = parseSort(sort);

    const response = await this.axios.get<StrapiApiResponse<SerializedInventoryMeterReadingGroup>>('', {
      params: buildQuery<SerializedInventoryMeterReadingGroup>({
        filters: {
          ...(inventoryMeterId && { inventory_meters: { id: { $eq: inventoryMeterId } } }),
          ...(location && { inventory_location: { id: { $eq: location } } }),
          ...(id && Number.isInteger(Number(id)) && { id: { $eq: Number(id) } }),
          ...(name && { name: { $containsi: name } }),
        },
        // @ts-expect-error should work with enmon meters
        populate: {
          inventory_location: {
            fields: ['id', 'name'],
          },
          inventory_meters: {
            fields: ['id', 'monitored_entity', 'commodity'],
            populate: {
              inventory_location_building: {
                fields: ['id', 'name'],
              },
            },
          },
        },
        sort: parsedSort,
        ...(current && {
          pagination: {
            page: current,
            pageSize: pageSize || 10,
          },
        }),
      }),
      signal,
    });

    return response.data;
  }

  public async create({ data }: CreateInventoryMeterReadingGroupArg): Promise<SerializedInventoryMeterReadingGroup> {
    const response = await this.axios.post<SerializedInventoryMeterReadingGroup>('', { data });
    return response.data;
  }

  public async update({
    id,
    data,
  }: UpdateInventoryMeterReadingGroupArg): Promise<SerializedInventoryMeterReadingGroup> {
    const response = await this.axios.put<SerializedInventoryMeterReadingGroup>(`${id}`, { data });
    return response.data;
  }

  public async delete(id: SerializedInventoryMeterReadingGroup['id']): Promise<void> {
    await this.axios.delete(`${id}`);
  }
}
